<template>
  <div>
    <!-- Header -->
    <AppHeader />

    <!-- Hero Section -->
    <section class="hero hero-eastern">
      <div class="container">
        <div class="hero-content fade-in-up">
          <h1>Курсы HSK</h1>
          <p>Подготовка к экзамену HSK</p>
        </div>
      </div>
    </section>

    <!-- Main Content -->
    <section class="content-section">
      <div class="container">
        <div class="content-wrapper">
          <h2>Что такое экзамен HSK?</h2>
          <p>
            <strong>HSK</strong> (Hanyu Shuiping Kaoshi) – стандартизированный квалификационный экзамен по китайскому языку. Проводится в Институтах Конфуция по всему миру. В письменном (бумажном) варианте и некоторых Институтах Конфуция возможно сдать электронный (компьютерный вариант). Существует 6 уровней экзамена. Сложность возрастает от уровня к уровню. Вместе с ней растет и количество слов необходимых для сдачи экзамена:
          </p>
          
          <div class="hsk-image-container">
            <img :src="hskWordsImage" alt="Количество слов на каждом HSK экзамене" class="hsk-words-image" />
          </div>

          <p>
            Мы подготовили для вас курсы подготовки к HSK, которые написаны на базе самого эффективного учебника для подготовки к экзамену – Standard Course. Дополнили практическими советами, интерактивными играми и краткими видео роликами к каждому блоку экзамена.
          </p>

          <!-- Онлайн курсы подготовки к HSK -->
          <div class="courses-section" v-if="categoriesLoaded">
            <h3>Онлайн курсы подготовки к HSK</h3>
            <CoursesList v-if="hskCategoryIds.length > 0" :category-ids="hskCategoryIds" />
            <p v-else class="no-courses">Нет доступных курсов в этой категории</p>
          </div>

          <h3>В основном, все HSK экзамены состоят из трех частей:</h3>
          <ul class="features-list">
            <li>аудирование</li>
            <li>чтение</li>
            <li>письмо</li>
          </ul>

          <p>
            Но с каждым уровнем присутствуют некоторые изменения. Например, начиная c HSK 5, аудирование слушаем всего 1 раз и вместо коротких текстов, нам предстоит прослушать целую историю. Письменная часть – не пара предложений, а два сочинения. Одно пишется по картинке, а второе – используя 5 данных слов. Длина сочинения составляет 80 знаков.
          </p>

          <p>
            HSK 6 максимально улетает в космос, и в письменной части заменяет сочинение на изложение по тексту, который вам дают прочитать в течении 10 минут, далее вам необходимо написать изложение по данной теме.
          </p>

          <!-- Пробные HSK тесты -->
          <div class="courses-section" v-if="categoriesLoaded">
            <h3>А еще у нас есть пробные HSK тесты..</h3>
            <p>
              Если вы хотите быть погрузиться в атмосферу реальной сдачи экзамена, с тикающим таймером и волнением о том, что вы можете не успеть, то мы для вас перенесли на нашу платформу и адаптировали некоторые из вариантов HSK. Самое то, чтобы попрактиковаться перед сдачей экзамена!
            </p>
            <CoursesList v-if="hskTestCategoryIds.length > 0" :category-ids="hskTestCategoryIds" />
            <p v-else class="no-courses">Нет доступных курсов в этой категории</p>
          </div>

          <!-- Зачем покупать курсы -->
          <div class="benefits-section">
            <h3>Зачем мне покупать курсы?</h3>
            <ul class="benefits-list">
              <li>
                <strong>Для экономии времени.</strong> Вы не тратите свое время на поиск литературы, пробных тестов, советов.
              </li>
              <li>
                <strong>Для плана.</strong> Который пригодится для системной подготовки к экзамену.
              </li>
              <li>
                <strong>Чтобы не грустить.</strong> Игры, онлайн карточки и разнообразные форматы заданий помогут разнообразить твою подготовку к экзамену.
              </li>
            </ul>
            <p>
              А со всем списком доступных курсов можно ознакомиться по ссылочке ниже. Заходи почаще, мы всегда работаем над новыми проектами.
            </p>
            <div class="cta-link">
              <router-link to="/courses" class="cta-button">Тут все наши курсы!</router-link>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <AppFooter />
  </div>
</template>

<script>
import AppHeader from '../components/AppHeader.vue';
import AppFooter from '../components/ui/UIFooter.vue';
import CoursesList from '../components/CoursesList.vue';
import hskWordsImage from '../assets/images/hsk-words.png';
import axios from 'axios';

export default {
  name: 'HSKCourses',
  components: {
    AppHeader,
    AppFooter,
    CoursesList
  },
  data() {
    return {
      hskWordsImage,
      hskCategoryIds: [],
      hskTestCategoryIds: [],
      categoriesLoaded: false
    };
  },
  async mounted() {
    await this.loadCategories();
  },
  methods: {
    async loadCategories() {
      try {
        // Сначала пробуем найти через поиск по видимым категориям
        const allCategoriesResponse = await axios.get('/api/v1/public/product-categories');
        const allCategories = allCategoriesResponse.data;
        
        // Находим категорию HSK (основные курсы) - по slug "hsk"
        let hskCategory = allCategories.find(cat => 
          cat.slug === 'hsk' || 
          (cat.slug.includes('hsk') && !cat.slug.includes('test'))
        );
        
        // Если не нашли в видимых, пробуем найти без проверки видимости
        if (!hskCategory) {
          try {
            const hskSlugs = ['hsk'];
            for (const slug of hskSlugs) {
              try {
                const response = await axios.get(`/api/v1/public/product-categories/by-slug/${slug}`);
                hskCategory = response.data;
                break;
              } catch (e) {
                // Пробуем следующий slug
              }
            }
          } catch (e) {
            console.log('Не удалось найти категорию HSK по slug');
          }
        }
        
        this.hskCategoryIds = hskCategory ? [hskCategory.id] : [];
        
        // Находим категорию для пробных тестов
        let testCategory = allCategories.find(cat => 
          cat.slug === 'test-hsk' || 
          cat.slug === 'hsk-test' ||
          (cat.slug.includes('hsk') && cat.slug.includes('test'))
        );
        
        // Если не нашли в видимых, пробуем найти без проверки видимости
        if (!testCategory) {
          try {
            const testSlugs = ['test-hsk', 'hsk-test'];
            for (const slug of testSlugs) {
              try {
                const response = await axios.get(`/api/v1/public/product-categories/by-slug/${slug}`);
                testCategory = response.data;
                break;
              } catch (e) {
                // Пробуем следующий slug
              }
            }
          } catch (e) {
            console.log('Не удалось найти категорию test-hsk по slug');
          }
        }
        
        this.hskTestCategoryIds = testCategory ? [testCategory.id] : [];
        
        this.categoriesLoaded = true;
      } catch (error) {
        console.error('Ошибка загрузки категорий:', error);
        this.categoriesLoaded = true; // Разрешаем показ, даже если была ошибка
      }
    }
  }
};
</script>

<style scoped>
/* Hero Section */
.hero {
  background: linear-gradient(135deg, #e63946 0%, #d62839 100%);
  color: white;
  padding: 80px 20px;
  text-align: center;
}

.hero-eastern {
  background: linear-gradient(135deg, #e63946 0%, #d62839 100%),
              url('https://images.unsplash.com/photo-1548013146-72479768bada?w=1600') center/cover;
  background-blend-mode: overlay;
}

.hero-content {
  max-width: 800px;
  margin: 0 auto;
}

.hero h1 {
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 16px;
  animation: fadeInUp 0.8s ease;
}

.hero p {
  font-size: 20px;
  opacity: 0.95;
  animation: fadeInUp 1s ease;
}

/* Content Section */
.content-section {
  padding: 60px 20px;
}

.content-wrapper {
  max-width: 900px;
  margin: 0 auto;
}

.content-wrapper h2 {
  font-size: 36px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 24px;
  margin-top: 0;
}

.content-wrapper h3 {
  font-size: 28px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 20px;
  margin-top: 40px;
}

.content-wrapper p {
  font-size: 18px;
  line-height: 1.8;
  color: #4b5563;
  margin-bottom: 24px;
}

.content-wrapper strong {
  color: #111827;
  font-weight: 700;
}

.hsk-image-container {
  margin: 32px 0;
  text-align: center;
}

.hsk-words-image {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
}

.features-list,
.benefits-list {
  list-style: none;
  padding: 0;
  margin: 24px 0;
}

.features-list li,
.benefits-list li {
  font-size: 18px;
  line-height: 1.8;
  color: #4b5563;
  margin-bottom: 12px;
  padding-left: 28px;
  position: relative;
}

.features-list li::before {
  content: '•';
  position: absolute;
  left: 0;
  color: #e63946;
  font-size: 24px;
  line-height: 1.2;
}

.benefits-list li::before {
  content: '✓';
  position: absolute;
  left: 0;
  color: #10b981;
  font-size: 20px;
  font-weight: 700;
}

.courses-section {
  margin: 48px 0;
}

.benefits-section {
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  padding: 40px;
  border-radius: 12px;
  margin-top: 48px;
}

.no-courses {
  text-align: center;
  color: #6b7280;
  font-style: italic;
  padding: 20px;
}

.cta-link {
  margin-top: 32px;
  text-align: center;
}

.cta-button {
  display: inline-block;
  padding: 14px 32px;
  background: #e63946;
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 18px;
  transition: all 0.3s ease;
}

.cta-button:hover {
  background: #d62839;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(230, 57, 70, 0.3);
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.fade-in-up {
  animation: fadeInUp 0.8s ease;
}

/* Responsive */
@media (max-width: 768px) {
  .hero h1 {
    font-size: 32px;
  }

  .hero p {
    font-size: 16px;
  }

  .content-wrapper h2 {
    font-size: 28px;
  }

  .content-wrapper h3 {
    font-size: 24px;
  }

  .content-wrapper p {
    font-size: 16px;
  }

  .benefits-section {
    padding: 24px;
  }
}
</style>

